import { useState } from 'react';
import {
  Breadcrumb,
  Card,
  Col,
  Container,
  ErrorMessage,
  Row,
  SummaryList,
  TextInput,
} from 'nhsuk-react-components';
import { useParams } from 'react-router-dom';
import SummaryRow from 'components/SummaryTable/Campaign/SummaryRow.tsx';
import ProgressIndicator from 'components/ProgressIndicator';
import useFetchTeamDetails from 'queries/fetch/fetchTeamDetails.ts';
import ReviewCohortsSection from 'pages/admin/TeamCohortsPage/ReviewCohortsSection.tsx';
import useFetchTeamCohorts from 'queries/fetch/fetchTeamCohorts.ts';
import { CohortResponse } from 'queries/fetch/fetchAllCohorts.ts';

interface ITeamCohortsPageContentProps {
  teamCohorts?: CohortResponse;
  cohortFilter: string;
  isLoadingTeamCohorts: boolean;
  teamCohortsError: Error | null;
  teamId: string;
}

function ReviewCohortsPageContent({
  teamCohorts,
  cohortFilter,
  isLoadingTeamCohorts,
  teamCohortsError,
  teamId,
}: ITeamCohortsPageContentProps) {
  if (isLoadingTeamCohorts) {
    return (
      <Card>
        <ProgressIndicator text="Loading cohorts..." />
      </Card>
    );
  }

  if (teamCohortsError) {
    return (
      <Card>
        <ErrorMessage>{teamCohortsError.message}</ErrorMessage>
      </Card>
    );
  }

  return (
    <ReviewCohortsSection
      teamCohorts={teamCohorts?.cohorts.CohortRefData ?? []}
      lowerCaseCohortFilter={cohortFilter.toLowerCase().trim()}
      teamId={teamId}
    />
  );
}
function ReviewTeamCohortsPage() {
  const { teamId } = useParams() as { teamId: string };

  const {
    data: teamCohorts,
    error: teamCohortsError,
    isLoading: isLoadingTeamCohorts,
  } = useFetchTeamCohorts({
    teamId,
  });

  const teamDetails = useFetchTeamDetails({ teamId });

  const [cohortFilter, setCohortFilter] = useState<string>('');

  return (
    <Container>
      <div className="nhsuk-u-margin-bottom-6">
        <Breadcrumb>
          <Breadcrumb.Item href="/">
            Home
          </Breadcrumb.Item>
          <Breadcrumb.Item href="/admin">
            Admin home
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span aria-current="page">Team cohorts</span>
          </Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <Card>
        <>
          <h1
            role="main"
            id="maincontent"
            className="nhsuk-heading-l nhsuk-u-margin-top-0 nhsuk-u-margin-bottom-3"
          >
            Team cohorts
          </h1>
          <p>Below is a list of cohorts for the selected team.</p>
        </>
      </Card>
      <Card>
        <>
          <Card.Heading className="nhsuk-heading-s">Team details</Card.Heading>
          <SummaryList>
            <SummaryRow
              itemKey="team-name"
              itemName="Team name"
              itemValue={teamDetails.data?.team.TEAM_NAME ?? teamId}
              canUpdate={false}
            />
          </SummaryList>
          <p className="nhsuk-u-font-weight-bold" id="searchLabel">
            Filter and search for cohorts using keywords like “Autumn”:
          </p>
          <Row>
            <Col width="full">
              <TextInput
                aria-labelledby="searchLabel"
                onChange={(e) => setCohortFilter((e.target as HTMLInputElement).value)}
                value={cohortFilter}
                id="cohort-filter"
              />
            </Col>
          </Row>
          <ReviewCohortsPageContent
            teamCohorts={teamCohorts}
            cohortFilter={cohortFilter}
            isLoadingTeamCohorts={isLoadingTeamCohorts}
            teamCohortsError={teamCohortsError}
            teamId={teamId}
          />
        </>
      </Card>
    </Container>
  );
}

export default ReviewTeamCohortsPage;
